import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { ArrowRight, Wrench } from "lucide-react";
import Seo from "../components/Seo";
import { filArianeJsonLd } from "../utils/donneesStructurees";

/**
 * Vitrine des artisans partenaires.
 *
 * Les logos arrivent au fil de l'eau : la page est en ligne avant eux, avec
 * des emplacements neutres. Pour intégrer un partenaire, il suffit de
 * renseigner `nom` et `logo` dans PARTENAIRES ci-dessous et de déposer le
 * fichier dans `public/partenaires/` — le rendu bascule tout seul de
 * l'emplacement vide au logo.
 *
 * `baseline` est facultatif : il sert aux agences qui partagent le même
 * bloc-marque. Le logo de l'enseigne est alors affiché tel quel et l'agence
 * est nommée en petit dessous, plutôt que de fabriquer un logo par agence.
 *
 * Tant que `logo` est vide, la carte n'affiche aucun nom d'entreprise : on ne
 * veut pas annoncer un partenaire dont le logo n'est pas encore validé.
 */
const PARTENAIRES = [
  { nom: "Valavie", logo: "/partenaires/logo_valavie.png" },
  { nom: "DOMet VIE Agence Pilote", logo: "/partenaires/logo_dometvie.png", baseline: "Agence Pilote" },
  { nom: "DOMet VIE Paris-Ouest - GM'ADAPT", logo: "/partenaires/logo_dometvie.png", baseline: "Paris-Ouest - GM'ADAPT" },
  { nom: "BAIN ADAPT", logo: "/partenaires/logo_bainadapt.png" },
  { nom: "BATABAT", logo: "/partenaires/logo_batabat.png" },
  { nom: "SANI ADAPT'", logo: "/partenaires/logo_sani.png" },
  { nom: "SVA France", logo: "/partenaires/logo_sva.png" },
  { nom: "THB Concept", logo: "/partenaires/logo_thb.png" },
  { nom: "ma douche.fr", logo: "/partenaires/logo_lge.png" },
  { nom: "BAT ADAPT", logo: "/partenaires/logo_batadapt.png" },
  { nom: "SOLUGAZ", logo: "/partenaires/logo_solugaz.png" },
];

const Partenaires = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Indexable depuis le 6 octobre 2026. Le noindex datait de l'époque où
          la grille n'était que des cadres pointillés vides — du contenu mince.
          Les onze logos réels sont en place : la page affiche désormais de
          vraies entreprises partenaires, elle renforce la confiance et
          l'ancrage local, et son maintien en noindex contredisait le sitemap
          (qui la déclare), ce qui remontait en erreur dans Search Console. */}
      <Seo
        titre="Nos artisans partenaires MaPrimeAdapt' Île-de-France | TEF"
        description="Découvrez les artisans partenaires de Transition Énergie France pour vos travaux d'adaptation du logement financés par MaPrimeAdapt'."
        chemin="/partenaires"
        donnees={[
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "Nos artisans partenaires", url: "/partenaires" },
          ]),
        ]}
      />
      <Header />

      <main id="contenu">
        {/* Hero */}
        <section className="bg-gradient-to-br from-white via-gray-50 to-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                Nos artisans partenaires
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed">
                Des professionnels qualifiés qui interviennent sur vos chantiers
                d&apos;adaptation du logement en Île-de-France
              </p>
            </div>
          </div>
        </section>

        {/* Grille de logos */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <ul className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 list-none p-0">
              {PARTENAIRES.map((partenaire, index) => (
                <li key={index}>
                  {partenaire.logo ? (
                    <div className="h-40 flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-gray-100 bg-white p-6 shadow-sm">
                      <img
                        src={partenaire.logo}
                        alt={partenaire.nom}
                        loading="lazy"
                        className="max-h-20 max-w-full object-contain"
                      />
                      {/* aria-hidden : l'agence est déjà dans l'alt du logo,
                          la lire deux fois n'apprendrait rien. */}
                      {partenaire.baseline && (
                        <span aria-hidden="true" className="text-xs font-medium text-gray-600 text-center">
                          {partenaire.baseline}
                        </span>
                      )}
                    </div>
                  ) : (
                    /* Emplacement en attente du logo. aria-hidden : il
                       n'apporte rien à qui n'a pas l'image sous les yeux. */
                    <div
                      aria-hidden="true"
                      className="h-40 flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50"
                    >
                      <Wrench className="h-8 w-8 text-gray-400" />
                      <span className="text-sm font-medium text-gray-500">Partenaire</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Appel aux artisans */}
        <section className="py-20 bg-gray-50 border-t-4 border-green-700">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Vous êtes artisan ? Rejoignez notre réseau
              </h2>
              <p className="text-lg text-gray-700">
                Plombier, menuisier, électricien ou installateur de monte-escalier en
                Île-de-France : nous montons le dossier MaPrimeAdapt&apos; de vos clients
                de bout en bout, sans commission sur vos devis.
              </p>
              <div className="pt-2">
                <Link to="/acces-pro">
                  <Button
                    size="lg"
                    className="bg-green-700 hover:bg-green-800 text-white shadow-lg group"
                  >
                    Découvrir l&apos;espace artisans
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Partenaires;
